import { useEffect, useRef, useCallback } from 'react';
import { useSecuritySettings } from './useSecuritySettings';
import { emitLogout } from '@/lib/auth/sessionCoordinator';

interface UseAutoLogoutOptions {
  enabled?: boolean;
  timeoutMinutes?: number;
  onLogout?: () => void;
}

export const useAutoLogout = (options: UseAutoLogoutOptions = {}) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  // Fetch security settings from DB (single source of truth)
  // Falls back to localStorage if API fails (offline support)
  const { data: securitySettings } = useSecuritySettings();

  // Get settings: priority: options > DB > localStorage > defaults
  const enabled = options.enabled ?? securitySettings?.autoLogoutEnabled ?? localStorage.getItem('autoLogoutEnabled') !== 'false';
  const timeoutMinutes = options.timeoutMinutes ?? securitySettings?.autoLogoutTimeout ?? parseInt(localStorage.getItem('autoLogoutTimeout') || '30', 10);
  const timeoutMs = timeoutMinutes * 60 * 1000;

  // Debug log (dev only)
  useEffect(() => {
    if (import.meta.env.DEV) {
      console.log('[AutoLogout] Settings:', {
        enabled,
        timeoutMinutes,
        timeoutMs,
        source: securitySettings ? 'DB' : 'localStorage/fallback',
        dbSettings: securitySettings
      });
    }
  }, [enabled, timeoutMinutes, timeoutMs, securitySettings]);

  const handleLogout = useCallback(() => {
    emitLogout('useAutoLogout:timeout');

    if (options.onLogout) {
      options.onLogout();
    }
  }, [options]);

  const resetTimer = useCallback(() => {
    if (!enabled) return;
    
    lastActivityRef.current = Date.now();
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    timeoutRef.current = setTimeout(() => {
      const token = localStorage.getItem('token');
      if (token) {
        console.log('[AutoLogout] Session expired due to inactivity');
        handleLogout();
      }
    }, timeoutMs);
  }, [enabled, timeoutMs, handleLogout]);

  useEffect(() => {
    if (!enabled) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      return;
    }

    // Activity events to track
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];

    // Throttle activity updates
    let throttleTimer: NodeJS.Timeout | null = null;
    const throttledResetTimer = () => {
      if (throttleTimer) return;
      throttleTimer = setTimeout(() => {
        throttleTimer = null;
        resetTimer();
      }, 1000); // Throttle to once per second
    };

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, throttledResetTimer, { passive: true });
    });

    // Initial timer
    resetTimer();

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, throttledResetTimer);
      });
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (throttleTimer) {
        clearTimeout(throttleTimer);
      }
    };
  }, [enabled, resetTimer, timeoutMs]); // Add timeoutMs to dependencies to re-init when settings change

  return {
    resetTimer,
    lastActivity: lastActivityRef.current
  };
};
